import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { ActivityStore } from '@state/activity.store';
import { UserProfileStore } from '@state/user-profile.store';
import { PrivacyStore } from '@state/privacy.store';
import { OnboardingStateService } from '@core/services/onboarding-state.service';
import { AuthService } from '../services/AuthService';
import { ToolbarComponent } from './toolbar/toolbar';
import { BottomNavComponent } from './bottom-nav/bottom-nav';
import { AppFooterComponent } from './app-footer/app-footer';
import { HealthNavWidgetComponent } from '../features/health-nav/health-nav-widget.component';

type BottomNavItem = {
  label: string;
  icon: string;
  route: string;
  match?: 'exact' | 'prefix';
};

@Component({
  selector: 'hc-mobile-app-shell-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    MatToolbarModule,
    MatButtonModule,
    ToolbarComponent,
    BottomNavComponent,
    AppFooterComponent,
    HealthNavWidgetComponent,
  ],
  templateUrl: './mobile-shell-component.html',
  styleUrls: ['./mobile-shell-component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileShellComponent implements OnDestroy {
  private readonly router = inject(Router);
  // Current route
  currentRoute = signal('');

  // Keep these routes aligned with your architecture.
  readonly bottomNav = signal<BottomNavItem[]>([
    { label: 'Home', icon: 'home', route: '/home', match: 'exact' },
    { label: 'Categories', icon: 'grid_view', route: '/categories', match: 'prefix' },
    { label: 'Connect', icon: 'people_alt', route: '/ask', match: 'exact' },
    { label: 'Activity', icon: 'history', route: '/me/activity', match: 'prefix' },
    { label: 'Me', icon: 'person', route: '/me/profile', match: 'prefix' },
  ]);

  // Top bar title can be computed from route, or provided via a page-title service later.
  readonly title = signal('Health Community');

  readonly activityStore = inject(ActivityStore);

  readonly activityUnread = computed(() => this.activityStore.unreadCount());

  readonly userProfileStore = inject(UserProfileStore);
  readonly privacyStore = inject(PrivacyStore);

  readonly currentUrl = signal(this.router.url);

  readonly showTopAppBar = computed(() => {
    // Example: hide top bar on auth/onboarding screens if they are outside shell;
    // if they are inside shell, you can gate here by route.
    return true;
  });

  readonly isLandingRoute = computed(() => {
    const url = this.url();
    return url === '/' || url.startsWith('/landing');
  });

  readonly url = signal(this.router.url);
  private lastAuth = false;

  constructor() {
    // Keep currentUrl in sync for active nav state
    // this.router.events
    //   .pipe(
    //     filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    //   )
    //   .subscribe(e => this.currentUrl.set(e.urlAfterRedirects));
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      this.url.set(this.router.url);
      this.currentUrl.set(this.router.url);
    });
    // Optional: update title from route data if you later add { data: { title: '...' } }
    effect(() => {
      // placeholder - keep a stable title by default
      // you can wire this to ActivatedRoute snapshots later.
      void this.currentUrl();
    });

    effect(() => {
      const authed = this.authService.isAuthenticated();
      if (authed && !this.lastAuth) {
        this.lastAuth = true;
        this.activityStore.refresh();
      }
      if (!authed) {
        this.lastAuth = false;
      }
    });

  }

  private readonly onboarding = inject(OnboardingStateService);
  private readonly authService = inject(AuthService);

  showIdentity() {
    return this.onboarding.onboardingDone && !this.isLandingRoute();
  }

  readonly showBottomNav = computed(() => {
    const url = this.url();
    const isAuthenticated = this.authService.isAuthenticated();

    if (url.startsWith('/onboarding') || url.startsWith('/accept-terms')) return false;
    return isAuthenticated;
  });

  readonly isBlockingTermsScreen = computed(() => this.url().startsWith('/accept-terms'));

  readonly showHealthNavWidget = computed(() => {
    const url = this.url();
    const isAuthenticated = this.authService.isAuthenticated();

    if (!isAuthenticated) return false;
    if (this.isLandingRoute() || this.isBlockingTermsScreen()) return false;
    if (url.startsWith('/onboarding') || url.startsWith('/login') || url.startsWith('/auth')) return false;

    return true;
  });

  readonly isPostDetailRoute = computed(() => {
    const path = this.url().split(/[?#]/, 1)[0];
    return new RegExp('^/posts?/[^/]+(?:/reply)?/?$').test(path);
  });

  ngOnDestroy(): void {
  }

  isActive(item: BottomNavItem): boolean {
    const url = this.currentUrl();
    const match = item.match ?? 'prefix';
    if (match === 'exact') return url === item.route;
    return url === item.route || url.startsWith(item.route + '/');
  }
}


